import Layout from "../../layout";
import "./dashboard.css";
import Wallet from "../../assets/images/wallet.png";
import RefundIcon from "../../assets/images/refund.png";
import Chart from "react-apexcharts";
import Button from "antd/lib/button";
import { FaArrowRight } from "react-icons/fa6";
import { AiOutlineLike } from "react-icons/ai";
import { AiOutlineDislike } from "react-icons/ai";
import commentData from "../../data/commentData";
import Rate from "antd/lib/rate";
import { useNavigate } from "react-router-dom";
import useDashboard from "../../hooks/use_dashboard";
import { Fragment, useEffect } from "react";
import Skeleton from "../../components/skeleton";
import { useSelector } from "react-redux";
import Empty from "antd/lib/empty";
import { CurrenciesEnum } from "../../utils/enums";
import formatHelper from "../../helpers/format_helper";
import moment from "moment";
import showFile from "../../helpers/show_file";
import { dateHelper } from "../../helpers/date_helper";
import { formatAmount } from "../../helpers/format_amount";

const Dashboard = () => {
  const navigate = useNavigate();

  const {
    // entity methods
    getDashboardOverview,
    getSaleChart,
    getIncomeChart,
    getEnrollmentsChart,
    getLastReviews,
    // entity states
    overviewData,
    saleChartData,
    incomeChartData,
    enrollmentsData,
    lastReviewsData,
    // loading states
    getDashboardOverviewLoading,
    getSaleChartLoading,
    getIncomeChartLoading,
    getEnrollmentsChartLoading,
    getLastReviewsLoading,
  } = useDashboard();

  const userProfile = useSelector((state) => state.app.userProfile);

  const options = {
    chart: {
      type: "donut",
      background: "transparent",
      width: "100%",
    },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
    labels: overviewData?.enrollmentByCourseId?.map((item) => {
      return item.courseName;
    }),
    colors: ["#062737ff", "#00679aff", "#4ebef6ff"],
    dataLabels: {
      enabled: true,
      formatter: (val, opts) =>
        `${opts.w.globals.labels[opts.seriesIndex]}: ${Math.round(val)}%`,
      style: {
        fontSize: "10px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: { enabled: false },
      offsetX: -20,
      textAnchor: "middle",
    },
    legend: {
      show: false,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      markers: {
        width: 16,
        height: 16,
        radius: 0,
        offsetX: -5,
        fillColors: ["#08384F", "#087BB3", "#AEDDF4"],
      },
      itemMargin: { horizontal: 10, vertical: 5 },
      formatter: function (seriesName, opts) {
        return `${seriesName}    ${opts.w.globals.series[opts.seriesIndex]}`;
      },
      onItemClick: { toggleDataSeries: false },
      onItemHover: { highlightDataSeries: false },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: false,
          },
        },
        dataLabels: {
          offset: -30,
          minAngleToShowLabel: 5,
        },
        customScale: 1,
        expandOnClick: false,
      },
    },
    tooltip: { enabled: false },
    stroke: { width: 0 },
  };

  const optionsSale = {
    chart: {
      type: "bar",
      background: "transparent",
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
      },
    },
    colors: ["#003488"],
    grid: {
      show: true,
      borderColor: "#f0f0f0",
      strokeDashArray: 0,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 5,
        columnWidth: "50%",
        dataLabels: {
          position: "top",
        },
        fill: {
          opacity: 1,
          colors: ["#008FFB"],
        },
      },
    },
    dataLabels: {
      offsetY: 12,
    },
    xaxis: {
      categories: saleChartData?.map((item) => {
        return moment(item.day).format("dd");
      }),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (value) => Math.round(value),
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
      axisBorder: {
        show: false,
      },
    },
    tooltip: {
      enabled: false,
    },
    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
  };

  const optionsIncome = {
    chart: {
      type: "line",
      background: "transparent",
      toolbar: { show: false },
      dropShadow: {
        enabled: true,
        top: 0,
        left: 0,
        blur: 6,
        color: "#000000",
        background: "#000000",
      },
    },
    colors: ["#67bf59", "#ff472e"],
    stroke: {
      width: 5,
      curve: "smooth",
    },
    markers: {
      size: 6,
      colors: ["#003488"],
      strokeColors: "#fff",
      strokeWidth: 2,
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      show: false,
    },
    xaxis: {
      categories: incomeChartData?.labels?.map((item) => {
        return moment(item).format("MMM");
      }),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
      title: {
        text: undefined,
      },
    },
    yaxis: {
      min: 0,
      tickAmount: 7,
      labels: {
        formatter: (val) => `${formatAmount(val)}`,
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
        offsetX: -12,
      },
      axisBorder: {
        show: false,
      },
      title: {
        text: undefined,
      },
    },
    states: { hover: { filter: { type: "none" } } },
  };

  useEffect(() => {
    if (userProfile) {
      getDashboardOverview();
      getSaleChart();
      getIncomeChart();
      getEnrollmentsChart();
      getLastReviews();
    }
  }, [userProfile]);

  return (
    <Layout>
      <div className="dashboard">
        {/* total_container */}
        <section className="total_container">
          {/* total_balance_mobile */}
          <div className="total_balance_mobile">
            <div>
              <img src={Wallet} alt="wallet" />
              <div>
                <p>Total Income</p>
                {getDashboardOverviewLoading ? (
                  <Fragment>
                    <Skeleton
                      style={{
                        width: 120,
                        height: 24,
                        borderRadius: 5,
                        marginBottom: 5,
                      }}
                    />
                    <Skeleton
                      style={{ width: 120, height: 24, borderRadius: 5 }}
                    />
                  </Fragment>
                ) : (
                  <Fragment>
                    {overviewData?.totalAmount?.map((item, index) => (
                      <h3
                        style={index == 0 ? { marginBottom: 5 } : {}}
                        key={index}
                      >
                        {
                          CurrenciesEnum?.find((o) => o.value == item.currency)
                            ?.label
                        }{" "}
                        {formatAmount(item.totalEarnings)}
                      </h3>
                    ))}
                  </Fragment>
                )}
              </div>
            </div>
          </div>
          {/* total refunds mobile */}
          <div className="total_balance_mobile">
            <div>
              <img style={{ width: 70 }} src={RefundIcon} alt="wallet" />
              <div>
                <p>Total Refunds</p>
                {getDashboardOverviewLoading ? (
                  <Fragment>
                    <Skeleton
                      style={{
                        width: 120,
                        height: 24,
                        borderRadius: 5,
                        marginBottom: 5,
                      }}
                    />
                    <Skeleton
                      style={{ width: 120, height: 24, borderRadius: 5 }}
                    />
                  </Fragment>
                ) : (
                  <Fragment>
                    {overviewData?.totalRefund?.map((item, index) => (
                      <h3
                        style={index == 0 ? { marginBottom: 5 } : {}}
                        key={index}
                      >
                        {
                          CurrenciesEnum?.find((o) => o.value == item.currency)
                            ?.label
                        }{" "}
                        {formatAmount(item.totalRefunds)}
                      </h3>
                    ))}
                  </Fragment>
                )}
              </div>
            </div>
            {/* <div>
              <Button disabled={getDashboardOverviewLoading}>Withdraw</Button>
              <Button disabled={getDashboardOverviewLoading}>Deposit</Button>
            </div> */}
          </div>
          {/* total income */}
          <div className="total_balance">
            <img src={Wallet} alt="wallet" />
            <p>Total Income</p>
            {getDashboardOverviewLoading ? (
              <Fragment>
                <Skeleton
                  style={{
                    width: 120,
                    height: 24,
                    borderRadius: 5,
                    marginBottom: 5,
                  }}
                />
                <Skeleton style={{ width: 120, height: 24, borderRadius: 5 }} />
              </Fragment>
            ) : (
              <Fragment>
                {overviewData?.totalAmount?.map((item, index) => (
                  <h3 style={index == 0 ? { marginBottom: 5 } : {}} key={index}>
                    {
                      CurrenciesEnum?.find((o) => o.value == item.currency)
                        ?.label
                    }{" "}
                    {formatAmount(item.totalEarnings)}
                  </h3>
                ))}
              </Fragment>
            )}
          </div>
          {/* total refunds */}
          <div id={"total_refunds"} className="total_balance">
            <img style={{ width: 100 }} src={RefundIcon} alt="refunds" />
            <p>Total Refunds</p>
            {getDashboardOverviewLoading ? (
              <Fragment>
                <Skeleton
                  style={{
                    width: 120,
                    height: 24,
                    borderRadius: 5,
                    marginBottom: 5,
                  }}
                />
                <Skeleton style={{ width: 120, height: 24, borderRadius: 5 }} />
              </Fragment>
            ) : (
              <Fragment>
                {overviewData?.totalRefund?.map((item, index) => (
                  <h3 style={index == 0 ? { marginBottom: 5 } : {}} key={index}>
                    {
                      CurrenciesEnum?.find((o) => o.value == item.currency)
                        ?.label
                    }{" "}
                    {formatAmount(item.totalRefunds)}
                  </h3>
                ))}
              </Fragment>
            )}
          </div>
          {/* total_center */}
          <div className="total_center">
            <div className="total_income">
              <div>
                <p>Last Income</p>
                {getDashboardOverviewLoading ? (
                  <Skeleton
                    style={{ width: 80, height: 24, borderRadius: 5 }}
                  />
                ) : (
                  <h3>
                    {overviewData?.lastIncome
                      ? CurrenciesEnum?.find(
                          (o) => o.value == overviewData?.lastIncome?.currency,
                        )?.label +
                        " " +
                        formatAmount(overviewData?.lastIncome?.amount)
                      : "----"}
                  </h3>
                )}
              </div>
              <div>
                <p>Total Reviews</p>
                {getDashboardOverviewLoading ? (
                  <Skeleton
                    style={{ width: 80, height: 24, borderRadius: 5 }}
                  />
                ) : (
                  <h3>{overviewData?.totalReview}</h3>
                )}
              </div>
              <div>
                <p>Total Students</p>
                {getDashboardOverviewLoading ? (
                  <Skeleton
                    style={{ width: 80, height: 24, borderRadius: 5 }}
                  />
                ) : (
                  <h3>{overviewData?.totalStudent}</h3>
                )}
              </div>
            </div>
          </div>
          {/* popural courses */}
          {getDashboardOverviewLoading ? (
            <Skeleton
              style={{ minWidth: 350, height: 290 }}
              className={"sale_chart pie_chart_container"}
            />
          ) : (
            <div className="sale_chart pie_chart_container">
              <div className="sale_chart_title">
                <h3>Popular courses</h3>
              </div>
              {overviewData?.enrollmentByCourseId?.length > 0 &&
              overviewData?.enrollmentByCourseId?.find(
                (o) => o.enrollmentCount > 0,
              ) ? (
                <div className="bar_chart pie_chart">
                  <Chart
                    options={options}
                    series={overviewData?.enrollmentByCourseId?.map((item) => {
                      return item.enrollmentCount ? item.enrollmentCount : 0;
                    })}
                    width={200}
                    type="pie"
                    height={"100%"}
                  />
                </div>
              ) : (
                <Empty
                  description="No popular courses found"
                  style={{ margin: "auto" }}
                />
              )}
            </div>
          )}
        </section>
        {/* sale_income_chart */}
        <section className="sale_income_chart">
          {getSaleChartLoading ? (
            <Skeleton className={"enrolled"} style={{ height: 380 }} />
          ) : (
            <div className="sale_chart">
              <div className="sale_chart_title">
                <h3>Sale</h3>
              </div>
              {saleChartData?.length > 0 ? (
                <div className="line_chart">
                  <Chart
                    options={optionsSale}
                    series={[
                      {
                        name: "Sales",
                        data: saleChartData.map((item) => {
                          return item.count;
                        }),
                      },
                    ]}
                    type="bar"
                    height={"100%"}
                  />
                </div>
              ) : (
                <Empty
                  description="No sale data found"
                  style={{ margin: "auto" }}
                />
              )}
            </div>
          )}
          {getIncomeChartLoading ? (
            <Skeleton className={"enrolled"} style={{ height: 380 }} />
          ) : (
            <div className="income_chart">
              <div className="Income_chart_title">
                <h3>Income</h3>
              </div>
              {incomeChartData ? (
                <div className="line_chart">
                  <Chart
                    options={optionsIncome}
                    series={incomeChartData.series}
                    type="line"
                    height={"100%"}
                  />
                </div>
              ) : (
                <Empty
                  description="No income data found"
                  style={{ margin: "auto" }}
                />
              )}
            </div>
          )}
        </section>
        {/* enrolled_comments */}
        <section className="enrolled_comments">
          {/* enrolled */}
          {getEnrollmentsChartLoading ? (
            <Skeleton className={"enrolled"} style={{ height: 355 }} />
          ) : (
            <div className="enrolled">
              {/* enrolled_title */}
              <div className="enrolled_title">
                <h3>People enrolled</h3>
                <Button onClick={() => navigate("/dashboard/members")}>
                  <span>All</span>
                  <FaArrowRight />
                </Button>
              </div>
              {enrollmentsData?.length > 0 ? (
                enrollmentsData.map((item, index) => (
                  <div key={index} className="progress_container">
                    <p>{item?.user?.fullname}</p>
                    <div className="progress_bar">
                      <div className="bar">
                        <div
                          style={{
                            width: `${
                              item.progress > 100 ? 100 : item.progress
                            }%`,
                          }}
                        ></div>
                      </div>
                      <span>{item.progress > 100 ? 100 : item.progress}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <Empty
                  description="No enrollments found"
                  style={{ margin: "auto", paddingTop: 50 }}
                />
              )}
            </div>
          )}
          {/* comments */}
          {getLastReviewsLoading ? (
            <Skeleton className={"enrolled"} style={{ height: 355 }} />
          ) : (
            <div className="comments">
              {/* comments_title */}
              <div className="comments_title">
                <h3>Last Comments</h3>
              </div>
              {lastReviewsData?.length > 0 ? (
                lastReviewsData.map((data, index) => (
                  <div key={index} className="comment_container">
                    {data?.user?.avatar ? (
                      <img src={showFile(data?.user?.avatar)} />
                    ) : (
                      <div>
                        {data?.user?.fullname?.slice(0, 1)?.toUpperCase()}
                      </div>
                    )}
                    <div className="comment_content">
                      <div>
                        <div>
                          <h4>{data.user?.fullname}</h4>
                          <span>{dateHelper(data.createTime)}</span>
                        </div>
                        <div className="rate_container_dashboard">
                          <Rate
                            disabled
                            defaultValue={data.rating?.toFixed(1)}
                          />
                        </div>
                      </div>
                      <p>{data.comment}</p>
                      <div>
                        <div className="rate_container_dashboard_mobile">
                          <Rate
                            disabled
                            defaultValue={data.rating?.toFixed(1)}
                          />
                        </div>
                        <div>{data.course?.name}</div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <Empty description="No comments found" />
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};
export default Dashboard;
